import React, { useState, useEffect } from 'react';

// ── Badge ──
export function Badge({ children, color = 'green', small }) {
  const colors = {
    green: { bg: 'var(--accent-dim)', text: 'var(--accent)', border: 'rgba(34,214,122,0.2)' },
    red: { bg: 'var(--red-dim)', text: 'var(--red)', border: 'rgba(239,68,68,0.2)' },
    amber: { bg: 'var(--amber-dim)', text: 'var(--amber)', border: 'rgba(245,158,11,0.2)' },
    blue: { bg: 'var(--blue-dim)', text: 'var(--blue)', border: 'rgba(59,130,246,0.2)' },
    neutral: { bg: 'var(--surface2)', text: 'var(--text2)', border: 'var(--border)' },
  };
  const c = colors[color] || colors.green;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: small ? '2px 8px' : '4px 12px',
      fontSize: small ? 11 : 12, fontWeight: 500, fontFamily: 'var(--mono)',
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
      borderRadius: 100, whiteSpace: 'nowrap', letterSpacing: '0.02em',
    }}>{children}</span>
  );
}

// ── Button ──
export function Button({ children, variant = 'primary', onClick, disabled, style: s, small, type = 'button' }) {
  const base = {
    fontFamily: 'var(--sans)', fontWeight: 600, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
    borderRadius: 'var(--radius)', transition: 'all 0.2s',
    fontSize: small ? 13 : 14, padding: small ? '8px 16px' : '12px 24px',
    opacity: disabled ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 8,
  };
  const variants = {
    primary: { ...base, background: 'var(--accent)', color: '#000', ...s },
    secondary: { ...base, background: 'var(--surface2)', color: 'var(--text)', border: '1px solid var(--border)', ...s },
    ghost: { ...base, background: 'transparent', color: 'var(--text2)', ...s },
    danger: { ...base, background: 'var(--red-dim)', color: 'var(--red)', border: '1px solid rgba(239,68,68,0.2)', ...s },
  };
  return <button style={variants[variant]} onClick={onClick} disabled={disabled} type={type}>{children}</button>;
}

// ── Input ──
export function Input({ label, placeholder, type = 'text', value, onChange, prefix, mono, name, required }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && (
        <label style={{ fontSize: 12, color: 'var(--text2)', fontFamily: 'var(--mono)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {label} {required && <span style={{ color: 'var(--red)' }}>*</span>}
        </label>
      )}
      <div style={{ display: 'flex', alignItems: 'center', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
        {prefix && <span style={{ padding: '0 0 0 14px', color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: 15 }}>{prefix}</span>}
        <input
          type={type} placeholder={placeholder} value={value} onChange={onChange} name={name} required={required}
          style={{
            width: '100%', padding: '12px 14px', background: 'transparent', border: 'none', outline: 'none',
            color: 'var(--text)', fontSize: 14, fontFamily: mono ? 'var(--mono)' : 'var(--sans)',
          }}
        />
      </div>
    </div>
  );
}

// ── Select ──
export function Select({ label, value, onChange, children, name, required }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && (
        <label style={{ fontSize: 12, color: 'var(--text2)', fontFamily: 'var(--mono)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {label} {required && <span style={{ color: 'var(--red)' }}>*</span>}
        </label>
      )}
      <select
        value={value} onChange={onChange} name={name} required={required}
        style={{
          padding: '12px 14px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
          color: 'var(--text)', fontSize: 14, fontFamily: 'var(--sans)', outline: 'none', appearance: 'none', cursor: 'pointer',
        }}
      >
        {children}
      </select>
    </div>
  );
}

// ── Textarea ──
export function Textarea({ label, placeholder, value, onChange, name, rows = 4 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && <label style={{ fontSize: 12, color: 'var(--text2)', fontFamily: 'var(--mono)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</label>}
      <textarea
        placeholder={placeholder} value={value} onChange={onChange} name={name} rows={rows}
        style={{
          width: '100%', padding: '12px 14px', background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', color: 'var(--text)', fontSize: 14, fontFamily: 'var(--sans)',
          outline: 'none', resize: 'vertical',
        }}
      />
    </div>
  );
}

// ── Stat Card ──
export function Stat({ label, value, color }) {
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: 24,
    }}>
      <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 800, color: color || 'var(--text)', fontFamily: 'var(--sans)' }}>{value}</div>
    </div>
  );
}

// ── Toast notifications ──
let toastTimeout;
export function showToast(message, type = 'success') {
  const existing = document.querySelector('.toast');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.className = `toast toast-${type}`;
  toast.textContent = message;
  document.body.appendChild(toast);

  clearTimeout(toastTimeout);
  toastTimeout = setTimeout(() => toast.remove(), 4000);
}

// ── Loading Spinner ──
export function Spinner({ size = 20 }) {
  return (
    <div style={{
      width: size, height: size, border: '2px solid var(--border)',
      borderTopColor: 'var(--accent)', borderRadius: '50%',
      animation: 'spin 0.6s linear infinite',
    }} />
  );
}

// Add spinner keyframe
if (typeof document !== 'undefined' && !document.getElementById('spinner-style')) {
  const style = document.createElement('style');
  style.id = 'spinner-style';
  style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}
